<template>
  <div class="payment-link-step-confirm">
    <div class="step-description">
      <p>{{ t('Revise os dados antes de criar o link de pagamento') }}</p>
    </div>

    <div class="confirmation-card">
      <!-- Amount -->
      <div class="confirm-section main-amount">
        <div class="section-value">{{ formatCurrency(amount / 100) }}</div>
        <div class="section-label">{{ t('Valor total') }}</div>
      </div>

      <!-- Details -->
      <div class="confirm-details">
        <!-- Payment Methods -->
        <div class="detail-row">
          <span class="detail-label">
            <i class="pi pi-credit-card"></i>
            {{ t('Formas de pagamento') }}
          </span>
          <span class="detail-value">{{ getSelectedMethodsLabel() }}</span>
        </div>

        <!-- Installments -->
        <div class="detail-row" v-if="chargeType === 'installment' && installments > 1">
          <span class="detail-label">
            <i class="pi pi-wallet"></i>
            {{ t('Parcelamento') }}
          </span>
          <span class="detail-value">
            {{ t('Até') }} {{ installments }}x de {{ formatCurrency(amount / installments / 100) }}
          </span>
        </div>

        <!-- Due Date -->
        <div class="detail-row" v-if="dueDate">
          <span class="detail-label">
            <i class="pi pi-calendar"></i>
            {{ t('Vencimento') }}
          </span>
          <span class="detail-value">{{ formatDate(dueDate) }}</span>
        </div>
        <div class="detail-row" v-else>
          <span class="detail-label">
            <i class="pi pi-calendar"></i>
            {{ t('Vencimento') }}
          </span>
          <span class="detail-value detail-muted">{{ t('Sem vencimento') }}</span>
        </div>

        <!-- Description -->
        <div class="detail-row" v-if="description">
          <span class="detail-label">
            <i class="pi pi-file"></i>
            {{ t('Descrição') }}
          </span>
          <span class="detail-value">{{ description }}</span>
        </div>

        <!-- Total -->
        <div class="detail-row total-row">
          <span class="detail-label">
            <i class="pi pi-dollar"></i>
            {{ t('Total') }}
          </span>
          <span class="detail-value total-value">{{ formatCurrency(amount / 100) }}</span>
        </div>
      </div>
    </div>

    <div class="confirm-notice">
      <i class="pi pi-info-circle"></i>
      <span>{{ t('Ao finalizar, o link será criado e você poderá enviá-lo ao cliente') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateFormatter } from '../../../composables/useDateFormatter';

interface PaymentMethodOption {
  ID: string;
  Name: string;
}

interface PaymentLinkFormData {
  amount?: number;
  payment_methods?: string[];
  charge_type?: 'single' | 'installment';
  installments?: number;
  due_date?: string | null;
  description?: string;
  [key: string]: unknown;
}

const { t } = useI18n();
const { formatDate: formatDateLocale } = useDateFormatter();

// Payment method options for labels
const paymentMethodOptions: PaymentMethodOption[] = [
  { ID: 'pix', Name: 'PIX' },
  { ID: 'boleto', Name: 'Boleto' },
  { ID: 'credit_card', Name: t('Cartão de Crédito') }
];

const props = defineProps<{
  value: PaymentLinkFormData;
}>();

// Computed values from value (read-only — confirm step does not mutate)
const amount = computed<number>(() => props.value.amount || 0);
const paymentMethods = computed<string[]>(() => props.value.payment_methods || []);
const chargeType = computed<'single' | 'installment'>(() => props.value.charge_type || 'single');
const installments = computed<number>(() => props.value.installments || 1);
const dueDate = computed<string | null>(() => props.value.due_date || null);
const description = computed<string>(() => props.value.description || '');

// Methods
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return formatDateLocale(date);
};

const getSelectedMethodsLabel = (): string => {
  const methods = paymentMethods.value;
  if (methods.length === 0) return '-';
  if (methods.length === 3) return t('Todas as formas');

  return methods.map(m => {
    const method = paymentMethodOptions.find(pm => pm.ID === m);
    return method ? method.Name : m;
  }).join(', ');
};

// Validation (confirmation step is always valid if we got here)
const isValid = computed<boolean>(() => true);

// Expose validation for wizard
defineExpose({
  isValid
});
</script>

<style scoped>
.payment-link-step-confirm {
  padding: 1rem 0;
}

.step-description {
  margin-bottom: 1.5rem;
  color: var(--text-color-secondary);
}

.step-description p {
  margin: 0;
}

.confirmation-card {
  background-color: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  overflow: hidden;
}

.main-amount {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-600));
  color: white;
  padding: 2rem;
  text-align: center;
}

.section-value {
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.5rem;
}

.section-label {
  font-size: 0.875rem;
  opacity: 0.9;
}

.confirm-details {
  padding: 1.5rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--surface-border);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color-secondary);
  font-size: 0.875rem;
}

.detail-label i {
  font-size: 1rem;
  width: 1.25rem;
  text-align: center;
}

.detail-value {
  font-weight: 600;
  color: var(--text-color);
  text-align: right;
}

.detail-muted {
  font-weight: normal;
  color: var(--text-color-secondary);
  font-style: italic;
}

.total-row {
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 2px solid var(--surface-border);
  border-bottom: none;
}

.total-row .detail-label {
  font-weight: 600;
  color: var(--text-color);
  font-size: 1rem;
}

.total-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--primary-color);
}

.confirm-notice {
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: var(--blue-50);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--blue-700);
  font-size: 0.875rem;
}

.confirm-notice i {
  font-size: 1.25rem;
  flex-shrink: 0;
}
</style>
